import { useCallback } from 'react';
import { showServerErrorToast, showNotificationToast } from 'src/components/toast/toast-helper';
import {
  BaseScoreSection,
  BaseVariableSection,
  StudyNoteConfigCreationDto,
  StudyNoteConfigPatchingDto,
} from 'src/services/api/api-client';
import { ReactComponent as NewCommentIcon } from 'assets/img/notification/new_comment_20.svg';
import { SaveFormParams } from '../../builder/generalFormBuilder.t';
import { useQueryClient } from '@tanstack/react-query';
import { useCreateFormConfigModal } from '../../monitoringForms/hooks/useCreateFormConfigModal';
import {
  createStudyNotesConfig,
  patchStudyNotesConfig,
  removeStudyNotesConfig,
  uploadConfigFile,
} from 'src/services/api/api-client/StudyNotesClient';
import { getStudyNotesConfigQueryKey } from 'src/services/api/api-client/StudyNotesQuery';
import { useScopedTranslation } from 'src/application/localisation/useScopedTranslation';
import { useStudy } from 'src/helpers/hooks/useStudy';

export const useStudyNoteHandlers = (deps: {
  selectedConfigId?: number;
  setConfigId: (id?: number) => void;
  initialTitle: string;
}) => {
  const { selectedConfigId, setConfigId: setSelectedConfig, initialTitle } = deps;

  const study = useStudy();
  const { t } = useScopedTranslation('GeneralFormBuilder.Toasts');
  const queryClient = useQueryClient();
  const createConfigModal = useCreateFormConfigModal();

  const deleteHandler = useCallback(async () => {
    try {
      await removeStudyNotesConfig(study!.id);
      setSelectedConfig(undefined);
      await queryClient.invalidateQueries(getStudyNotesConfigQueryKey(study!.id));

      showNotificationToast(t('FormDeletedToast.Message'), t('FormDeletedToast.Header'), NewCommentIcon, 3000);
    } catch (error) {
      showServerErrorToast(error);
    }
  }, [queryClient, setSelectedConfig, study, t]);

  const createFormHandler = useCallback(
    async (params: SaveFormParams, newTitle: string) => {
      try {
        const { nodes, scoreSections, variables } = params;
        const created = await createStudyNotesConfig(
          study!.id,
          new StudyNoteConfigCreationDto({
            layout: nodes,
            title: newTitle,
            scoreSections: scoreSections?.map((x) => new BaseScoreSection(x)) ?? null,
            variables: variables?.map((x) => new BaseVariableSection(x)) ?? null,
          }),
        );

        await queryClient.invalidateQueries(getStudyNotesConfigQueryKey(study!.id));

        setSelectedConfig(created.id);
        showNotificationToast(t('FormSavedToast.Message'), t('FormSavedToast.Header'), NewCommentIcon, 3000);
      } catch (error) {
        showServerErrorToast(error);
      }
    },
    [study, setSelectedConfig, queryClient, t],
  );

  const patchFormConfigHandler = useCallback(
    async (params: SaveFormParams) => {
      if (!selectedConfigId) throw new Error('selectedConfigId has not provided');
      try {
        const { nodes, scoreSections, variables } = params;
        await patchStudyNotesConfig(
          study!.id,
          new StudyNoteConfigPatchingDto({
            layout: nodes,
            title: initialTitle,
            scoreSections: scoreSections?.map((x) => new BaseScoreSection(x)) ?? null,
            variables: variables?.map((x) => new BaseVariableSection(x)) ?? null,
          }),
        );

        await queryClient.invalidateQueries(getStudyNotesConfigQueryKey(study!.id));
        showNotificationToast(t('FormSavedToast.Message'), t('FormSavedToast.Header'), NewCommentIcon, 3000);
      } catch (error) {
        showServerErrorToast(error);
      }
    },
    [initialTitle, queryClient, selectedConfigId, study, t],
  );

  const saveAsHandler = useCallback(
    async (params: SaveFormParams) => {
      await createConfigModal.open({
        callback: (data) => createFormHandler(params, data.title),
        initialValues: { title: initialTitle },
      });
    },
    [createConfigModal, createFormHandler, initialTitle],
  );

  const onSave = useCallback(
    async (params: SaveFormParams) => {
      if (!selectedConfigId) await saveAsHandler(params);
      else await patchFormConfigHandler(params);
    },
    [selectedConfigId, saveAsHandler, patchFormConfigHandler],
  );

  const uploadFileFunction = useCallback(
    (localFile: any) =>
      uploadConfigFile(study!.id, {
        data: localFile.file,
        fileName: localFile.fileName,
      }),
    [study],
  );

  const builderProps = {
    onDelete: selectedConfigId ? deleteHandler : undefined,
    onSave: onSave,
    // NOTE: onSaveAs = undefined, because the Study note config is a single and can't be copied
    onSaveAs: undefined,
    uploadFileFunction,
  };

  return { builderProps, createConfigModal: createConfigModal.modal };
};
